import apiClient from './apiClient';
import type { CycleRole } from './cyclesService';

export type Dow = 1 | 2 | 3 | 4 | 5 | 6 | 7;
/** R41: the recovery is no longer a phase of its own; it is the tail of the PLAN allocation. */
export type Phase = 'PLAN' | 'BACKUP';
export type Preparation = 'IDEAL' | 'REDUCED' | 'CONDENSED';
export type RaceMode = 'ENROLL' | 'CONTINUE' | 'MIGRATE';

export interface AthleteSettings {
  /** sportId → N sessions per week, non-PPG sports only. */
  sessionsPerWeek: Record<string, number>;
  sportDays: Array<{ dow: Dow; sportId: string }>;
  brickDow?: Dow | null;
  includePpg: boolean;
}

export interface CycleReduction {
  cycleId: string;
  cycleName: string;
  planCycleOrder: number;
  baselineWeeks: number;
  actualWeeks: number;
}

export interface Recap {
  planId: string;
  planName: string;
  formatCode: string;
  planType: string;
  competitionDate?: string;
  planStartDate: string;
  preparation?: Preparation;
  idealWeeks: number;
  actualWeeks: number;
  reductions: CycleReduction[];
  testsRemoved: number;
  settings?: AthleteSettings;
  sessionsPerWeek: { min: number; max: number };
  /** R43: rest weeks are counted apart so they do not drag the range down to zero. */
  restWeeks: number;
  weeklyVolume: Array<{ weekStart: string; phase: 'PLAN' | 'BACKUP'; bySport: Record<string, number> }>;
}

export interface EngineWarning {
  code: string;
  count?: number;
  detail?: Record<string, unknown>;
  phase: Phase;
  message: string;
}

export interface TraceLine {
  code: string;
  planWeekIndex?: number;
  cycleId?: string;
  weekNumber?: number;
  cycleSessionId?: string;
  detail?: Record<string, unknown>;
  message: string;
  phase?: Phase;
}

export interface PreparationTexts {
  kind: Preparation | null;
  title: string | null;
  body: string | null;
  reductions: string[];
  closing: string | null;
  testsRemoved: string | null;
}

export interface PreviewPayload {
  planStartDate: string;
  backup?: { planId: string; planName: string; from: string; to: string };
  recovery?: { from: string; to: string; weeks: number };
  preparation?: Preparation;
  reductions: CycleReduction[];
  testsRemoved: number;
  recap: Recap;
  warnings: EngineWarning[];
  messages: { preparation: PreparationTexts; dminWeeks: number; dmaxWeeks: number };
}

export interface SimulationItem {
  date: string;
  sport: { id: string; label: string } | null;
  workout: { id: string; name: string; durationSec: number } | null;
  priority: number | null;
  isTest: boolean;
  dayOfWeek: number | null;
  /** The solver moved the session away from its preferred day. */
  moved: boolean;
  linkKey: string | null;
  linkOrder: number | null;
  cycleSessionId: string;
  /** Format to render the workout with; null when the session's cycle does not scale. */
  scalingFormat: string | null;
}

export interface SimulationDropped {
  cycleSessionId: string;
  scalingFormat: string | null;
  workout: { id: string; name: string };
  sport: { id: string; label: string };
  priority: number;
  dayOfWeek: number | null;
  reason: string;
  reasonLabel: string;
}

export interface SimulationWeek {
  phase: Phase;
  weekStart: string;
  planWeekIndex: number;
  cycle: { id: string; name: string; planCycleOrder: number };
  cycleRole: CycleRole;
  sourceWeekNumber: number;
  items: SimulationItem[];
  dropped: SimulationDropped[];
  trace: TraceLine[];
}

export interface SimulationPayload extends PreviewPayload {
  weeks: SimulationWeek[];
  planTrace: TraceLine[];
  volumeSportIds: string[];
  /** D27: the coach note of the plan's race type, shown on the competition cell. */
  competitionCoachNote: string | null;
}

export interface SimulateInput {
  planId: string;
  competitionDate?: string;
  today?: string;
  mode?: RaceMode;
  /** Simulates an athlete still finishing a previous objective's recovery (R33). */
  earliestStart?: string;
  settings?: AthleteSettings;
}

export interface SettingsFormSport {
  sportId: string;
  label: string;
  min: number;
  max: number;
  default: number;
}

export interface SettingsForm {
  planId: string;
  dayMode: 'ATHLETE' | 'DAYS_ONLY' | 'FIXED';
  sports: SettingsFormSport[];
  ppg: { available: boolean; sportId: string };
  hasBricks: boolean;
}

export type FeasibilityScenario = 'WITH_BRICK' | 'WITHOUT_BRICK';

export type CheckSettingsResult =
  | { ok: true; feasibility: { feasible: boolean; scenario?: FeasibilityScenario; suggestedSportIds?: string[] } }
  | { ok: false; error: { code: string; scenario?: FeasibilityScenario; sportIds?: string[]; message: string } };

export interface DateBounds {
  condensedFromWeek: string | null;
  reducedFromWeek: string;
  idealFromWeek: string;
  latestWeek: string;
  dminWeeks: number;
  dmaxWeeks: number;
  condensedMinWeeks: number | null;
  recoveryEndsOn: string | null;
}

export interface EngineErrorBody {
  message?: string;
  error?: {
    code: string;
    phase?: Phase;
    issues?: { code: string; cycleId?: string; weekNumber?: number; detail?: Record<string, unknown> }[];
    earliestCompetitionWeek?: string;
    latestCompetitionWeek?: string;
    sportIds?: string[];
    scenario?: FeasibilityScenario;
  };
}

export const simulate = async (input: SimulateInput): Promise<SimulationPayload> => {
  const response = await apiClient.post('admin/engine/simulate', input);
  return response.data as SimulationPayload;
};

export const getDateBounds = async (
  planId: string,
  today?: string,
  recoveryEndsOn?: string
): Promise<DateBounds | { bounds: null }> => {
  const params: Record<string, string> = {};
  if (today) params.today = today;
  if (recoveryEndsOn) params.recoveryEndsOn = recoveryEndsOn;
  const response = await apiClient.get(`engine/plans/${planId}/date-bounds`, { params });
  return response.data as DateBounds | { bounds: null };
};

export const getSettingsForm = async (planId: string): Promise<SettingsForm> => {
  const response = await apiClient.get(`engine/plans/${planId}/settings-form`);
  return response.data as SettingsForm;
};

export const checkSettings = async (planId: string, settings: AthleteSettings): Promise<CheckSettingsResult> => {
  const response = await apiClient.post(`engine/plans/${planId}/check-settings`, { settings });
  return response.data as CheckSettingsResult;
};
